import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminRoomDetail,
  updateYowanAdminRoom,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const FieldNote = ({ children }) => {
  if (!children) {
    return null;
  }

  return (
    <p
      className="yowan-admin-form__field-note yowan-admin-form__field-note--compact"
    >
      {children}
    </p>
  );
};

const AdminRoomEditorFields = ({
  content,
  register,
  errors,
  facilityLocked = false,
  lockedFacilityName = '',
}) => {
  const { labels, options, helpText, errors: formErrors } = content.form;

  return (
    <>
      <div className="c-form__field">
        <span className="c-form__label">
          {labels.roomName}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('roomName', { required: formErrors.required })}
        />
        {errors.roomName ? <em className="yowan-admin-form__error">{errors.roomName.message}</em> : null}
        <FieldNote>{helpText.roomName}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.facilityId}</span>
        {facilityLocked ? (
          <>
            <input type="hidden" {...register('facilityId')} />
            <input className="c-form__control" type="text" value={lockedFacilityName} disabled readOnly />
          </>
        ) : (
          <select
            className="c-form__control"
            {...register('facilityId')}
          >
            {(options.facilities || []).map((option) => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        <FieldNote>{helpText.facilityId}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.capacity}</span>
        <input className="c-form__control" type="number" min="0" {...register('capacity')} />
        <FieldNote>{helpText.capacity}</FieldNote>
      </div>

      <div className="c-form__field yowan-admin-form__toggle-field">
        <span className="c-form__label">{labels.isActive}</span>
        <label className="c-form__choice yowan-admin-form__toggle-choice">
          <input type="checkbox" {...register('isActive')} />
          <span>{labels.isActive}</span>
        </label>
        <FieldNote>{helpText.isActive}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.remarks}</span>
        <textarea className="c-form__control" rows="4" {...register('remarks')} />
      </div>
    </>
  );
};

const AdminRoomsEditPage = () => {
  const content = yowanPageContent['/admin/events/rooms/:roomId/edit'];
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const { roomId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord || {
        roomName: '',
        facilityId: '',
        capacity: '',
        isActive: true,
        remarks: '',
      },
  });

  useEffect(() => {
    if (!content || !roomId) {
      return undefined;
    }

    let isMounted = true;

    const loadRoom = async () => {
      setIsLoading(true);
      setSubmitMessage('');

      try {
        const record = await fetchYowanAdminRoomDetail(roomId);

        if (!isMounted) {
          return;
        }

        setCurrentRecord(record);
        reset(record);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setCurrentRecord(null);
        setSubmitMessage('部屋を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRoom();

    return () => {
      isMounted = false;
    };
  }, [content, roomId, reset]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await updateYowanAdminRoom(roomId, values);
      setSubmitMessage('保存しました。');
      navigate(appendYowanPreviewQueryIfNeeded('/admin/events/rooms', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage('保存できませんでした。入力内容とログイン状態を確認してください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/events/rooms" routeKey="/admin/events/rooms/:roomId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/rooms', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      {isLoading || !currentRecord ? (
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner size="sm" /> : submitMessage || '部屋が見つかりませんでした。'}
        </div>
      ) : (
        <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
          <AdminRoomEditorFields
            content={content}
            register={register}
            errors={errors}
            facilityLocked
            lockedFacilityName={currentRecord.facilityName}
          />

          <div className="yowan-admin-form__actions">
            <button type="submit" className="c-form__submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  {content.form.pendingMessage}
                </>
              ) : content.form.submitLabel}
            </button>
          </div>

          {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
        </form>
      )}
    </AdminBlogShell>
  );
};

export default AdminRoomsEditPage;
